import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { cognitoErrorCode, cognitoErrorMessage } from "../lib/auth.js";
import {
  AuthForm,
  AuthShell,
  ErrorBanner,
  fieldCls,
  labelCls,
  linkCls,
} from "../components/AuthShell.js";
import { Button } from "../components/ui/Button.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel } from "../lib/signupCopy.js";

export function LoginPage() {
  const { mode: signupMode } = useSignupMode();
  const { signIn, startGoogleSignIn, googleEnabled, configured } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      const code = cognitoErrorCode(err);
      if (code === "UserNotConfirmedException") {
        navigate(`/verify?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      if (code === "NotAuthorizedException") {
        setError("Wrong email or password.");
      } else if (code === "UserNotFoundException") {
        setError("No account found for that email.");
      } else {
        setError(cognitoErrorMessage(err, "Sign-in failed. Please try again."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setGoogleBusy(true);
    setError(null);
    try {
      await startGoogleSignIn();
    } catch (err) {
      setError(cognitoErrorMessage(err, "Could not start Google sign-in."));
      setGoogleBusy(false);
    }
  }

  if (!configured) {
    return (
      <AuthShell title="Configuration missing" subtitle="Cognito env vars are not set.">
        <p className="text-sm text-muted">
          Run <code className="rounded-lg bg-slate-100 px-1">deploy.ps1</code> to generate{" "}
          <code className="rounded-lg bg-slate-100 px-1">web/.env.local</code>.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Sign in" subtitle="Checklists you reuse, share, and finish together.">
      <AuthForm onSubmit={handleSubmit}>
        <ErrorBanner message={error} />

        <div>
          <label htmlFor="email" className={labelCls}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className={fieldCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <label htmlFor="password" className={labelCls}>
              Password
            </label>
            <Link to="/forgot" className={`text-xs ${linkCls}`}>
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            className={fieldCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <Button type="submit" fullWidth disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>

        {googleEnabled ? (
          <>
            <div className="relative py-1 text-center text-xs text-muted-sm">
              <span className="bg-surface relative z-10 px-2">or</span>
              <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line" />
            </div>
            <Button
              type="button"
              variant="secondary"
              fullWidth
              disabled={googleBusy}
              onClick={() => void handleGoogle()}
            >
              {googleBusy ? "Redirecting…" : "Continue with Google"}
            </Button>
          </>
        ) : null}

        <p className="pt-1 text-center text-sm text-muted">
          New here?{" "}
          <Link to="/register" className={linkCls}>
            {signupLabel("login", signupMode)}
          </Link>
        </p>
      </AuthForm>
    </AuthShell>
  );
}
